import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Plus, Trash2, AlertCircle } from 'lucide-react';
import { MusicIcon } from '../icons';
import { Button } from '../ui';
import { CreateProjectRequest } from '../../services';
import './BulkCreatePieces.css';

interface BulkCreatePiecesProps {
  opusId: string;
  opusTitle: string;
  projectType: string;
  onSubmit: (pieces: Omit<CreateProjectRequest, 'workflowTemplateId'>[]) => Promise<void>;
  onCancel: () => void;
  isLoading?: boolean;
}

interface PieceEntry {
  id: string;
  title: string;
}

export const BulkCreatePieces: React.FC<BulkCreatePiecesProps> = ({
  opusId,
  opusTitle,
  projectType,
  onSubmit,
  onCancel,
  isLoading = false,
}) => {
  const { t } = useTranslation();
  const [pieces, setPieces] = useState<PieceEntry[]>([
    { id: crypto.randomUUID(), title: '' },
  ]);
  const [bulkMode, setBulkMode] = useState(false);
  const [bulkText, setBulkText] = useState('');
  const [error, setError] = useState<string | null>(null);

  const addPiece = () => {
    setPieces([...pieces, { id: crypto.randomUUID(), title: '' }]);
  };

  const removePiece = (id: string) => {
    if (pieces.length > 1) {
      setPieces(pieces.filter((p) => p.id !== id));
    }
  };

  const updatePieceTitle = (id: string, title: string) => {
    setPieces(pieces.map((p) => (p.id === id ? { ...p, title } : p)));
  };

  const parseBulkText = () => {
    const titles = bulkText
      .split('\n')
      .map((line) => line.trim())
      .filter((line) => line.length > 0);

    if (titles.length === 0) {
      setError(t('works.pieces.bulkCreateDescription'));
      return;
    }

    setPieces(titles.map((title) => ({ id: crypto.randomUUID(), title })));
    setBulkMode(false);
    setBulkText('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    const validPieces = pieces.filter((p) => p.title.trim());
    if (validPieces.length === 0) {
      setError(t('works.form.title') + ' is required for at least one piece');
      return;
    }

    try {
      const pieceData = validPieces.map((p) => ({
        title: p.title.trim(),
        projectType,
        category: 'Piece' as const,
        parentProjectId: opusId,
        initialStage: 'Not Started', // Will be set by backend based on template
      }));

      await onSubmit(pieceData);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('works.errors.createFailed'));
    }
  };

  return (
    <div className="bulk-create-overlay">
      <div className="bulk-create-modal">
        <div className="bulk-create-modal__header">
          <div className="bulk-create-modal__title-section">
            <h2 className="bulk-create-modal__title">{t('works.pieces.bulkCreateTitle')}</h2>
            <span className="bulk-create-modal__opus-name">
              <MusicIcon size={14} />
              {opusTitle}
            </span>
          </div>
          <button className="bulk-create-modal__close" onClick={onCancel} aria-label="Close">
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          {error && (
            <div className="bulk-create-modal__error">
              <AlertCircle size={16} />
              {error}
            </div>
          )}

          <div className="bulk-create-modal__mode-toggle">
            <button
              type="button"
              className={`bulk-create-modal__mode-btn ${!bulkMode ? 'bulk-create-modal__mode-btn--active' : ''}`}
              onClick={() => setBulkMode(false)}
            >
              {t('works.pieces.modeIndividual')}
            </button>
            <button
              type="button"
              className={`bulk-create-modal__mode-btn ${bulkMode ? 'bulk-create-modal__mode-btn--active' : ''}`}
              onClick={() => setBulkMode(true)}
            >
              {t('works.pieces.modeBulkText')}
            </button>
          </div>

          {bulkMode ? (
            <div className="bulk-create-modal__bulk-section">
              <label>{t('works.pieces.bulkCreateDescription')}</label>
              <textarea
                value={bulkText}
                onChange={(e) => setBulkText(e.target.value)}
                placeholder={t('works.pieces.bulkCreatePlaceholder')}
                rows={8}
                autoFocus
              />
              <Button type="button" variant="secondary" onClick={parseBulkText}>
                {t('works.pieces.parseButton')}
              </Button>
            </div>
          ) : (
            <div className="bulk-create-modal__pieces-list">
              {pieces.map((piece, index) => (
                <div key={piece.id} className="bulk-create-modal__piece-row">
                  <span className="bulk-create-modal__piece-number">{index + 1}</span>
                  <input
                    type="text"
                    value={piece.title}
                    onChange={(e) => updatePieceTitle(piece.id, e.target.value)}
                    placeholder={t('works.pieces.pieceTitlePlaceholder', { number: index + 1 })}
                    maxLength={100}
                  />
                  <button
                    type="button"
                    className="bulk-create-modal__remove-btn"
                    onClick={() => removePiece(piece.id)}
                    disabled={pieces.length === 1}
                    aria-label="Remove piece"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              ))}
              <button
                type="button"
                className="bulk-create-modal__add-btn"
                onClick={addPiece}
              >
                <Plus size={16} />
                {t('works.pieces.addAnother')}
              </button>
            </div>
          )}

          <div className="bulk-create-modal__summary">
            <span className="bulk-create-modal__count">
              {t('works.pieces.countToCreate', { count: pieces.filter((p) => p.title.trim()).length })}
            </span>
          </div>

          <div className="bulk-create-modal__actions">
            <Button type="button" variant="ghost" onClick={onCancel}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" variant="primary" disabled={isLoading}>
              {isLoading ? t('common.loading') : t('common.create')}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BulkCreatePieces;
